import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { FormDrawer } from '@/components/feedback/drawer';
import { useToast } from '@/components/feedback/toast';
import { SelectField, TextAreaField, TextField } from '@/components/forms';

import { useOrderAssignees, useRaiseTicket } from '../hooks';
import { raiseTicketInputSchema } from '../schemas';
import type { OrderDetail, RaiseTicketInput } from '../types';

type Props = { order: OrderDetail; open: boolean; onClose: () => void };

export function RaiseTicketDrawer({ order, open, onClose }: Props) {
  const form = useForm<RaiseTicketInput>({
    resolver: zodResolver(raiseTicketInputSchema),
    defaultValues: { title: '', assigneeId: '', description: '' },
    mode: 'onBlur',
  });
  const assignees = useOrderAssignees(open);
  const options = useMemo(
    () => (assignees.data ?? []).map((a) => ({ value: a.id, label: a.name })),
    [assignees.data],
  );
  const raise = useRaiseTicket(order.id);
  const toast = useToast();

  const onSubmit = async (values: RaiseTicketInput) => {
    try {
      await raise.mutateAsync(values);
      toast.success({ title: 'Support ticket raised successfully.' });
      onClose();
    } catch (e) {
      toast.error({ title: e instanceof Error ? e.message : 'Could not raise the ticket.' });
    }
  };

  return (
    <FormDrawer
      form={form}
      onSubmit={onSubmit}
      open={open}
      onClose={onClose}
      title="Raise a Ticket"
      submitText="Submit"
      width={464}
    >
      <TextField name="title" control={form.control} label="Title" placeholder="Enter" required />
      <SelectField
        name="assigneeId"
        control={form.control}
        label="Choose Assignee"
        placeholder="Select"
        required
        loading={assignees.isFetching}
        options={options}
      />
      <TextAreaField
        name="description"
        control={form.control}
        label="Description"
        placeholder="Enter"
        required
        rows={5}
      />
    </FormDrawer>
  );
}
